import {includes, some} from 'lodash';
import React, {FC, PropsWithChildren} from 'react';
import {Navigate, Outlet, useOutletContext} from 'react-router-dom';
import {useUser} from './use-auth';
import {AllPermissions, Roles} from '../api/generated/enums';
import {ROUTES} from '../routes';

type Permissions = (typeof AllPermissions)[number];
type PermissionParam = Permissions | Permissions[];
type RoleParam = Roles | Roles[];

export type useRequireAuthorizationProps = PropsWithChildren<{
  strict?: boolean;
}> &
  (
    | {
        permission: PermissionParam;
        role?: RoleParam;
      }
    | {
        permission?: PermissionParam;
        role: RoleParam;
      }
    | {
        permission: PermissionParam;
        role: RoleParam;
      }
  );

export const useRequireAuthorization = ({
  permission,
  role,
  strict,
}: useRequireAuthorizationProps) => {
  const user = useUser();

  const permissions = Array.isArray(permission) ? permission : [permission];
  const roles = Array.isArray(role) ? role : [role];

  const hasPermission = some(permissions, (x) => includes(user.permissions, x));
  const hasRole = roles.includes(user.role);
  const isGlobalAdmin = user.role === 'Global Admin';

  return hasPermission || hasRole || (isGlobalAdmin && !strict);
};

export const RequireAuthorization: FC<useRequireAuthorizationProps> = ({
  children,
  ...rest
}) => {
  const isAuthorized = useRequireAuthorization({...rest});
  return <>{isAuthorized && children}</>;
};

type ProtectedRouteProps = useRequireAuthorizationProps;
export const ProtectedRoute: FC<ProtectedRouteProps> = (props) => {
  const isAuthorized = useRequireAuthorization(props);

  const outletContext = useOutletContext();

  return isAuthorized ? (
    <Outlet context={outletContext} />
  ) : (
    <Navigate to={ROUTES.DASHBOARD.path} />
  );
};

type ProtectedElementProps = PropsWithChildren<useRequireAuthorizationProps>;
export const ProtectedElement: FC<ProtectedElementProps> = (props) => {
  const isAuthorized = useRequireAuthorization(props);

  return isAuthorized ? <>{props.children}</> : null;
};